import { Reporter } from './reporter';
import { ErrorInfo, ErrorStats } from './types';

/**
 * Executor 类 - 安全执行函数并采集所有错误信息
 * 阻断所有错误，避免业务代码中断，并上报到服务器
 *
 * 使用示例：
 * ```typescript
 * import { Executor } from '@zaf/vite-plugin-trace';
 *
 * // 创建执行器实例
 * const executor = new Executor();
 *
 * // 安全执行同步函数
 * const result = executor.safeExecute(() => {
 *   // 可能抛出错误的代码
 *   return someRiskyOperation();
 * }, 'user-action');
 *
 * // 安全执行异步函数
 * const asyncResult = await executor.safeExecuteAsync(async () => {
 *   return await fetchData();
 * }, 'api-call');
 *
 * // 包装现有函数
 * const safeFunction = executor.wrapFunction(originalFunction, 'context');
 * const safeAsyncFunction = executor.wrapAsyncFunction(originalAsyncFunction, 'context');
 *
 * // 获取错误统计
 * const stats = executor.getErrorStats();
 * console.log(`总错误数: ${stats.totalErrors}`);
 * ```
 */
export class Executor {
  private reporter?: Reporter;
  private errorCache: ErrorInfo[] = [];
  private stats: ErrorStats;
  private aggregationTimer?: number;

  constructor() {
    this.stats = {
      totalErrors: 0,
      byCategory: {},
      bySeverity: {},
      recentErrors: [],
    };

    this.startErrorAggregation();
  }

  /**
   * 设置 Reporter 实例，用于错误上报
   */
  setReporter(reporter: Reporter): void {
    this.reporter = reporter;
  }

  /**
   * 安全执行同步函数，捕获所有错误并记录
   */
  safeExecute<T>(
    fn: () => T,
    context: string = 'unknown'
  ): T | undefined {
    try {
      return fn();
    } catch (error) {
      this.handleError(error, context, 'sync', 'high');
      return undefined;
    }
  }

  /**
   * 安全执行异步函数，捕获所有错误并记录
   */
  async safeExecuteAsync<T>(
    fn: () => Promise<T>,
    context: string = 'unknown'
  ): Promise<T | undefined> {
    try {
      return await fn();
    } catch (error) {
      this.handleError(error, context, 'async', 'high');
      return undefined;
    }
  }

  /**
   * 包装函数，返回包装后的安全函数
   */
  wrapFunction<T extends (...args: any[]) => any>(
    fn: T,
    context: string = 'unknown'
  ): T {
    return ((...args: Parameters<T>) => {
      try {
        const result = fn(...args);
        if (result instanceof Promise) {
          return result.catch((error) => {
            this.handleError(error, context, 'promise', 'high');
            return undefined;
          });
        }
        return result;
      } catch (error) {
        this.handleError(error, context, 'sync', 'high');
        return undefined;
      }
    }) as T;
  }

  /**
   * 包装异步函数，返回包装后的安全函数
   */
  wrapAsyncFunction<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    context: string = 'unknown'
  ): T {
    return (async (...args: Parameters<T>) => {
      try {
        return await fn(...args);
      } catch (error) {
        this.handleError(error, context, 'async', 'high');
        return undefined;
      }
    }) as T;
  }

  /**
   * 处理全局未捕获错误
   */
  handleGlobalError(event: ErrorEvent | PromiseRejectionEvent): void {
    const isErrorEvent = event instanceof ErrorEvent;
    const error = isErrorEvent ? event.error : event.reason;
    const context = isErrorEvent ? 'global-error' : 'unhandled-promise-rejection';
    const category = isErrorEvent ? 'sync' : 'promise';

    this.handleError(error, context, category, 'high');
  }

  /**
   * 获取错误统计信息
   */
  getErrorStats(): ErrorStats {
    return { ...this.stats };
  }

  /**
   * 清除错误缓存
   */
  clearErrorCache(): void {
    this.errorCache = [];
    this.stats.recentErrors = [];
  }

  /**
   * 内部错误处理方法
   */
  private handleError(
    error: unknown,
    context: string,
    category: ErrorInfo['category'],
    severity: ErrorInfo['severity']
  ): void {
    // 构造错误信息
    const errorInfo: ErrorInfo = {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      name: error instanceof Error ? error.name : 'UnknownError',
      timestamp: Date.now(),
      context,
      category,
      severity,
      userInfo: this.collectUserInfo(),
    };

    // 添加到缓存
    this.errorCache.push(errorInfo);
    if (this.errorCache.length > 100) {
      this.errorCache.shift(); // 移除最旧的错误
    }

    // 更新统计信息
    this.updateStats(errorInfo);

    // 调试模式下输出到控制台
    console.error(`[${category.toUpperCase()}] ${context}:`, error);
    if (error instanceof Error && error.stack) {
      console.error(error.stack);
    }

    // 上报错误
    if (this.reporter) {
      this.reportError(errorInfo);
    }
  }

  /**
   * 更新错误统计信息
   */
  private updateStats(errorInfo: ErrorInfo): void {
    this.stats.totalErrors++;

    // 按类别统计
    this.stats.byCategory[errorInfo.category] =
      (this.stats.byCategory[errorInfo.category] || 0) + 1;

    // 按严重程度统计
    this.stats.bySeverity[errorInfo.severity] =
      (this.stats.bySeverity[errorInfo.severity] || 0) + 1;

    // 最近错误列表
    this.stats.recentErrors.push(errorInfo);
    if (this.stats.recentErrors.length > 10) {
      this.stats.recentErrors.shift();
    }
  }

  /**
   * 收集用户信息
   */
  private collectUserInfo() {
    try {
      return {
        userId: (window as any).globalValue?.dataModule?.userInfo?.openId,
        sessionId: this.getSessionId(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
    } catch {
      return undefined;
    }
  }

  /**
   * 获取会话ID
   */
  private getSessionId(): string {
    try {
      return (window as any).traceManager?.getSessionId?.() || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * 上报错误信息
   */
  private async reportError(errorInfo: ErrorInfo): Promise<void> {
    if (!this.reporter) return;

    try {
      // 创建错误追踪数据
      const errorTraceData = {
        traceId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        serviceName: 'frontend-error-collector',
        serviceInstance: window.location.hostname,
        serviceVersion: '1.0.0',
        spans: [{
          spanId: 'error-span',
          operationName: `error-${errorInfo.category}`,
          startTime: errorInfo.timestamp,
          endTime: errorInfo.timestamp,
          duration: 0,
          tags: [
            { key: 'error.message', value: errorInfo.message },
            { key: 'error.name', value: errorInfo.name },
            { key: 'error.context', value: errorInfo.context },
            { key: 'error.category', value: errorInfo.category },
            { key: 'error.severity', value: errorInfo.severity },
            { key: 'error.stack', value: errorInfo.stack || '' },
            { key: 'user.url', value: errorInfo.userInfo?.url || '' },
            { key: 'user.userAgent', value: errorInfo.userInfo?.userAgent || '' },
          ],
          logs: [],
          isError: true,
        }],
        startTime: errorInfo.timestamp,
        endTime: errorInfo.timestamp,
        duration: 0,
      };

      await this.reporter.report(errorTraceData);
    } catch (reportError) {
      // 防止上报错误时的递归错误
      console.error('Failed to report error:', reportError);
    }
  }

  /**
   * 启动错误聚合定时器
   */
  private startErrorAggregation(): void {
    // 每分钟聚合一次错误
    this.aggregationTimer = window.setInterval(() => {
      this.aggregateAndReportErrors();
    }, 60000);
  }

  /**
   * 聚合并上报错误
   */
  private async aggregateAndReportErrors(): Promise<void> {
    if (this.errorCache.length === 0) return;

    // 这里可以实现错误聚合逻辑，比如按类型和时间聚合
    // 暂时先清空缓存，避免内存泄漏
    this.errorCache = [];
  }

  /**
   * 销毁执行器，清理资源
   */
  destroy(): void {
    if (this.aggregationTimer) {
      clearInterval(this.aggregationTimer);
      this.aggregationTimer = undefined;
    }
    this.clearErrorCache();
  }
}
